import * as React from "react";

export enum UsePortalDropzone {
  AfterLastChild = "///bbnt-use-portal-dropzone-after-last-child",
  BeforeFirstChild = "///bbnt-use-portal-dropzone-before-first-child",
}

export interface UsePortalProps {
  id?: string;
  selector?: string;
  dontCreateRootElementWhenNone?: boolean;
  dropzone?: `${UsePortalDropzone}`;

  portalStagingNodeClassName?: string;
}

/**
 * Hook to create a React Portal.
 * Automatically handles creating and tearing-down the root elements (no SRR
 * makes this trivial), so there is no need to ensure the parent target already
 * exists.
 * @example
 * const target = usePortal(id, [id]);
 * return createPortal(children, target);
 * @param {String} id The id of the target container, e.g 'modal' or 'spotlight'
 * @returns {HTMLElement} The DOM node to use as the Portal target.
 */
export const usePortal = (props: UsePortalProps) => {
  const rootElemRef = React.useRef<HTMLDivElement | null>(null);

  const thisDropzone = React.useMemo(() => {
    return props.dropzone || UsePortalDropzone.AfterLastChild;
  }, [props.dropzone]);

  React.useEffect(() => {
    const existingPortalTopLevelElement = document.querySelector(
      props.selector ? props.selector : `#${props.id}`
    ) as HTMLElement;

    const thePortalTopLevelElement =
      existingPortalTopLevelElement ||
      (!props.id || props.dontCreateRootElementWhenNone
        ? null
        : createRootElement(props.id));

    if (!existingPortalTopLevelElement && thePortalTopLevelElement) {
      appendsElementAsLastChildOfBody(thePortalTopLevelElement);
    }

    if (thePortalTopLevelElement && rootElemRef.current) {
      switch (thisDropzone) {
        case UsePortalDropzone.AfterLastChild:
          thePortalTopLevelElement.appendChild(rootElemRef.current);
          break;
        case UsePortalDropzone.BeforeFirstChild:
          thePortalTopLevelElement.insertBefore(
            rootElemRef.current,
            thePortalTopLevelElement.firstElementChild
          );
          break;
        default:
          break;
      }
    }

    return () => {
      if (rootElemRef.current) {
        rootElemRef.current.remove();
      }
    };
  }, [
    props.selector,
    props.id,
    props.dontCreateRootElementWhenNone,
    thisDropzone,
  ]);

  /**
   * It's important we evaluate this lazily:
   * - We need first render to contain the DOM element, so it shouldn't happen
   *   in useEffect. We would normally put this in the constructor().
   * - We can't do 'const rootElemRef = useRef(document.createElement('div))',
   *   since this will run every single render (that's a lot).
   * - We want the ref to consistently point to the same DOM element and only
   *   ever run once.
   * @link https://reactjs.org/docs/hooks-faq.html#how-to-create-expensive-objects-lazily
   */
  const getRootElem = () => {
    if (!rootElemRef.current) {
      rootElemRef.current = document.createElement("div");
      if (props.portalStagingNodeClassName) {
        rootElemRef.current.classList.add(props.portalStagingNodeClassName);
      }
    }
    return rootElemRef.current;
  };

  return getRootElem();
};

const createRootElement = (id: string) => {
  const rootContainer = document.createElement("div");
  rootContainer.setAttribute("id", id);
  return rootContainer;
};
const appendsElementAsLastChildOfBody = (rootElem: HTMLElement) => {
  document.body.insertBefore(
    rootElem,
    document?.body?.lastElementChild?.nextElementSibling || null
  );
};
